import React, { useCallback, useRef, useState } from 'react';
import { format } from 'date-fns/format';
import { differenceInDays } from 'date-fns';
import { getText } from '@storebrand-digital/fms-translations';
import { CardHeadingGroup, CardNav, Badge } from '@storebrand-digital/pretzel';
import { useQueryClient } from '@tanstack/react-query';
import type Document from './Document';
import { useKron } from '../../app/hooks/useKron';
import { fetchPDF } from '../Preview/hooks/usePDF';
import { generatePdfFilename } from '../../utils';
import { useDocumentContext } from './context';
import { useMarkDocumentAsRead } from './hooks';
import styles from './Document.CardItem.module.css';

interface Props extends Document {
  id: Document.Id;
}

function DocumentCardItem({
  id,
  sourceType,
  name,
  contractNumbers,
  products,
  isRead,
  date,
}: Props) {
  const isRecent = differenceInDays(new Date(), new Date(date)) <= 40;
  const queryClient = useQueryClient();
  const { showPdf } = useKron();
  const { selectedSegmentId } = useDocumentContext();
  const markAsRead = useMarkDocumentAsRead(selectedSegmentId);
  const [isLoading, setIsLoading] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const loadingTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const formattedDate = format(date, 'dd.MM.yyyy');

  const startLoading = useCallback(() => {
    setIsLoading(true);
    loadingTimerRef.current = setTimeout(() => setShowLoading(true), 300);
  }, []);

  const stopLoading = useCallback(() => {
    clearTimeout(loadingTimerRef.current);
    setIsLoading(false);
    setShowLoading(false);
  }, []);

  const descriptionParts: string[] = [];
  if (products?.length > 0) {
    descriptionParts.push(products.join(', '));
  }
  if (contractNumbers?.length > 0) {
    descriptionParts.push(
      `${getText('filter.contract.title')}: ${contractNumbers.join(', ')}`
    );
  }
  const description =
    descriptionParts.length > 0 ? (
      <>
        {descriptionParts.map((part, index) => (
          <React.Fragment key={index}>
            {index > 0 && <br />}
            {part}
          </React.Fragment>
        ))}
      </>
    ) : undefined;

  const handleClick = async () => {
    if (isLoading) {
      return;
    }

    startLoading();
    try {
      const blob = await queryClient.fetchQuery({
        queryKey: ['documents', id, 'pdf', sourceType],
        queryFn: () => fetchPDF(id, sourceType),
        staleTime: Infinity,
        gcTime: Infinity,
      });
      if (blob) {
        const base64PdfData = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () =>
            resolve((reader.result as string).split(',')[1]);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        const downloadFilename = generatePdfFilename({
          id,
          name,
          contractNumbers,
          date,
        });
        markAsRead(id);
        showPdf(downloadFilename, { type: 'base64', data: base64PdfData });
      }
    } finally {
      stopLoading();
    }
  };

  const badge =
    !isRead && isRecent ? (
      <Badge
        variant="info"
        density="compact"
        heading={getText('document.new.badge')}
      />
    ) : undefined;

  return (
    <CardNav
      orientation="vertical"
      fluid
      badge={badge}
      aria-label={name}
      aria-busy={showLoading}
      style={{
        opacity: showLoading ? 0.6 : 1,
        pointerEvents: isLoading ? 'none' : 'auto',
        transition: 'opacity 0.2s ease',
      }}
    >
      {(ref) => (
        <div className={styles.documentCardItemHeadingContainer}>
          <CardHeadingGroup
            ref={ref}
            as="button"
            onClick={handleClick}
            heading={name}
            description={description}
            data-trackid={
              sourceType === 'MAIN'
                ? 'button-show|Vis pdf Main'
                : 'button-show|Vis pdf Bank'
            }
          />
          <p className="font-key-number">{formattedDate}</p>
        </div>
      )}
    </CardNav>
  );
}

export default DocumentCardItem;
